/**
 * empleadosFaltantes.js
 *
 * Ex-empleados que la lista de raya necesita y que nunca se dieron de alta en Biwo:
 * "Importar Desde Software Externo" omite a propósito las filas de baja, así que sus
 * renglones salen como "No existe un empleado con código N". Aquí se cruzan contra el
 * Excel de empleados del mismo software externo para crearlos DESHABILITADOS y poder
 * cargarles la nómina histórica.
 *
 * Módulo puro (sin React ni XLSX): se prueba con un script de Node igual que
 * parseoListaRaya.js y mapeoExterno.js.
 *
 * Por qué se crean con `estado: 2` y SIN fechaBaja, aunque el archivo la traiga:
 *  - Con una fechaBaja anterior al periodo (p. ej. el finiquito pagado la semana
 *    siguiente a la baja) el motor deja en 0 los días de sueldo, los cotizados y la
 *    ventana de incidencias, y las fórmulas de importe1..4 —de donde salen el gravado
 *    y el exento— se evaluarían contra cero días.
 *  - El listado de Empleados pintaría "Finiquito pendiente" por un finiquito que ya se
 *    pagó en el sistema anterior.
 * Es el mismo `estado: 2` del botón Deshabilitar; para reactivarlo basta Habilitar.
 */

import {
  construirIndiceEncabezados,
  mapearFilaExterna,
  agruparAvisosCatalogo,
} from '../../Empleados/ImportarEmpleadosExterno/mapeoExterno'

// Misma normalización que construirIndicesEmpleadosNomina (parseoListaRaya.js): el
// "0050" del archivo empata con el 50 guardado en Empleado.codigo.
export const normalizarCodigo = (codigo) => {
  if (codigo === null || codigo === undefined) return null
  const texto = String(codigo).trim()
  if (!texto) return null
  const n = Number(texto)
  return Number.isFinite(n) ? String(n) : null
}

const nombreCompleto = (e) =>
  [e?.nombre, e?.apellidoPaterno, e?.apellidoMaterno].filter(Boolean).join(' ')

/**
 * Renglones de la lista de raya sin empleado cuyo código no existe en Biwo con
 * ningún estado. Un renglón con varios candidatos (mismo nombre) tampoco cuenta:
 * se resuelve eligiendo en la vista previa, no creando a otro. Y uno cuyo código SÍ
 * existe (otro renglón ya reclamó a ese empleado) no se arregla creando a nadie.
 *
 * @param preview    analisis.preview de analizarHoja
 * @param empleados  todos los empleados de la empresa (cualquier estado)
 * @returns {[{ codigo, nombreArchivo, filaExcel }]} uno por código
 */
export function codigosFaltantes(preview = [], empleados = []) {
  const enSistema = new Set(empleados.map((e) => normalizarCodigo(e.codigo)).filter(Boolean))
  const vistos = new Set()
  const faltantes = []
  for (const p of preview) {
    if (p.empleadoId || p.candidatos?.length) continue
    const codigo = normalizarCodigo(p.codigo)
    if (!codigo || enSistema.has(codigo) || vistos.has(codigo)) continue
    vistos.add(codigo)
    faltantes.push({ codigo, nombreArchivo: p.nombreArchivo, filaExcel: p.filaExcel })
  }
  return faltantes
}

/**
 * Cruza los faltantes contra las filas del Excel de empleados del software externo.
 *
 * @param filas      sheet_to_row_object_array de la hoja "Empleados"
 * @param catalogos  los de empleados/datosParaImportarEmpleados
 * @param faltantes  resultado de codigosFaltantes
 * @param empleados  todos los empleados de la empresa (cualquier estado), con rfc
 * @returns {{ aCrear, noEnArchivo, rfcExistente, conErrores, avisosCatalogo }}
 *   aCrear lleva `emp` (el payload de mapearFilaExterna, listo para el backend) más
 *   estatusExterno / esBaja / fechaBajaExterna para la vista previa.
 */
export function cruzarConArchivoExterno({ filas = [], catalogos = {}, faltantes = [], empleados = [] }) {
  const indice = construirIndiceEncabezados(filas)

  // Código → primera fila del archivo con ese código.
  const porCodigo = new Map()
  for (const fila of filas) {
    const r = mapearFilaExterna(fila, indice, catalogos, {}, { incluirBajas: true })
    const codigo = normalizarCodigo(r.emp?.numeroClave)
    if (codigo && !porCodigo.has(codigo)) porCodigo.set(codigo, r)
  }

  // RFC → quién lo tiene. Contra TODOS los empleados: si la persona ya está en Biwo
  // con otro código (activa o no), crearla otra vez la duplicaría.
  const rfcsTomados = new Map()
  for (const e of empleados) {
    if (e.rfc) rfcsTomados.set(String(e.rfc).trim().toUpperCase(), { empleado: e })
  }

  const aCrear = []
  const noEnArchivo = []
  const rfcExistente = []
  const conErrores = []
  const avisos = []

  for (const f of faltantes) {
    const r = porCodigo.get(f.codigo)
    if (!r) {
      noEnArchivo.push(f)
      continue
    }

    const base = { ...f, nombre: nombreCompleto(r.emp) || f.nombreArchivo, rfc: r.emp.rfc, numFila: r.numFila }

    if (r.erroresFila.length) {
      conErrores.push({ ...base, motivo: r.erroresFila.join(', ') })
      continue
    }

    // Sin errores el RFC viene validado y en mayúsculas (mapearFilaExterna).
    const dueno = rfcsTomados.get(r.emp.rfc)
    if (dueno) {
      const e = dueno.empleado
      rfcExistente.push({
        ...base,
        motivo: e
          ? `El RFC ya es de ${e.serieNumero || 'otro empleado'}${e.codigo != null ? ` (código ${e.codigo})` : ''} ${nombreCompleto(e)}${e.estado !== 1 ? ', deshabilitado' : ''}`
          : `El RFC se repite con el código ${dueno.codigoLote} de este mismo archivo`,
      })
      continue
    }
    rfcsTomados.set(r.emp.rfc, { codigoLote: f.codigo })

    aCrear.push({
      ...base,
      emp: r.emp,
      estatusExterno: r.estatusExterno,
      esBaja: r.esBaja,
      fechaBajaExterna: r.fechaBajaExterna,
    })
    avisos.push(...r.avisosCatalogo)
  }

  return {
    aCrear,
    noEnArchivo,
    rfcExistente,
    conErrores,
    avisosCatalogo: agruparAvisosCatalogo(avisos),
  }
}
